import { TimeRange } from 'features/systemLens'
import {
  loadDateConfigFromChart,
  loadDerivedValuesFromChart,
  loadSensorsFromChart,
  loadSourceConfigProperties,
} from 'features/timeSeries/utils/chartConfigLoaders'
import { Dispatch, SetStateAction, useCallback } from 'react'
import {
  BaseSensorEntry,
  ChartTimeUnit,
  DateMode,
  StandaloneChart,
  TimeSeriesDerivedValueEntryJSON,
} from 'types'

interface ApplyChartConfigParams {
  chart: StandaloneChart | null | undefined
  sensorEntries: BaseSensorEntry[]
  derivedValueEntries: TimeSeriesDerivedValueEntryJSON[]
  cadenceSeconds: number
  mode: 'merge' | 'replace'
  setSelectedSensors: Dispatch<SetStateAction<BaseSensorEntry[]>>
  setDerivedValues: Dispatch<SetStateAction<TimeSeriesDerivedValueEntryJSON[]>>
  setCustomSensorColors: Dispatch<SetStateAction<Record<string, string>>>
  setSensorVisibilityConfig: Dispatch<SetStateAction<Record<string, boolean>>>
  setCustomAxisRanges: Dispatch<
    SetStateAction<Record<string, { min?: number; max?: number }>>
  >
  setAutoScaleMode: Dispatch<SetStateAction<boolean>>
  setSingleScaleMode: Dispatch<SetStateAction<boolean>>
  setDateMode: Dispatch<SetStateAction<DateMode>>
  setTimeRange: Dispatch<SetStateAction<TimeRange>>
  setLiveTail: Dispatch<SetStateAction<boolean>>
  setRelativeStart: Dispatch<
    SetStateAction<{ value?: number; unit: ChartTimeUnit }>
  >
  setRelativeEnd: Dispatch<
    SetStateAction<{ isNow: boolean; value?: number; unit: ChartTimeUnit }>
  >
  setCadenceSeconds: Dispatch<SetStateAction<number>>
}

export const useApplyChartConfig = () => {
  const applyChartConfig = useCallback(
    ({
      chart,
      sensorEntries,
      derivedValueEntries,
      cadenceSeconds,
      mode,
      setSelectedSensors,
      setDerivedValues,
      setCustomSensorColors,
      setSensorVisibilityConfig,
      setCustomAxisRanges,
      setAutoScaleMode,
      setSingleScaleMode,
      setDateMode,
      setTimeRange,
      setLiveTail,
      setRelativeStart,
      setRelativeEnd,
      setCadenceSeconds,
    }: ApplyChartConfigParams) => {
      if (!chart) return

      const sensors = loadSensorsFromChart(chart, sensorEntries)
      if (sensors.length > 0 || mode === 'replace') {
        setSelectedSensors(sensors)
      }

      const derivedVals = loadDerivedValuesFromChart(chart, derivedValueEntries)
      if (derivedVals.length > 0 || mode === 'replace') {
        setDerivedValues(derivedVals)
      }

      if (chart.sourceConfigs.length > 0 || mode === 'replace') {
        const { colors, visibility, axisRanges, hasCustomRanges } =
          loadSourceConfigProperties(chart)

        if (mode === 'merge') {
          setCustomSensorColors((prev) => ({ ...prev, ...colors }))
          setSensorVisibilityConfig((prev) => ({ ...prev, ...visibility }))
          setCustomAxisRanges((prev) => ({ ...prev, ...axisRanges }))
        } else {
          setCustomSensorColors(colors)
          setSensorVisibilityConfig(visibility)
          setCustomAxisRanges(axisRanges)
        }

        if (hasCustomRanges) {
          setAutoScaleMode(false)
        } else if (mode === 'replace') {
          setAutoScaleMode(true)
        }
      }

      if (chart.yAxisMode) {
        setSingleScaleMode(chart.yAxisMode === 'single')
      }

      const dateConfig = loadDateConfigFromChart(chart, cadenceSeconds)
      if (dateConfig) {
        setDateMode(dateConfig.dateMode)
        setTimeRange(dateConfig.timeRange)
        setLiveTail(dateConfig.liveTail)
        setRelativeStart(dateConfig.relativeStart)
        setRelativeEnd(dateConfig.relativeEnd)
        setCadenceSeconds(dateConfig.cadenceSeconds)
      }
    },
    []
  )

  return { applyChartConfig }
}
